import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { openThreadCreator, type CreationSnapshot, type CreateSelection, type ThreadCreator } from "./create";
import { transcriptUrl } from "./urls";

export function CreateConversation({ selection }: { selection: CreateSelection }) {
  const key = JSON.stringify(selection);
  const [state, setState] = useState<{ key: string; creator: ThreadCreator; value: CreationSnapshot }>();
  const [name, setName] = useState("");
  const [error, setError] = useState(false);
  const [busy, setBusy] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);
  const [reload, setReload] = useState(0);
  const lifetime = useRef<AbortController | undefined>(undefined);
  const inFlight = useRef(false);
  const writes = useRef<Promise<CreationSnapshot> | undefined>(undefined);
  const editID = useRef(0);
  useEffect(() => {
    const controller = new AbortController(); lifetime.current = controller;
    setState(undefined); setError(false); setBusy(false); setSaveFailed(false);
    inFlight.current = false; writes.current = undefined; editID.current++;
    void openThreadCreator(selection, undefined, controller.signal).then(async (creator) => {
      const value = await creator.read(controller.signal);
      if (!controller.signal.aborted) { setState({ key, creator, value }); setName(value.name); }
    }).catch(() => { if (!controller.signal.aborted) setError(true); });
    return () => controller.abort();
  }, [key, reload]);
  const current = state?.key === key ? state : undefined;
  function edit(text: string) {
    if (!current || inFlight.current) return;
    setName(text);
    const serial = ++editID.current, signal = lifetime.current!.signal;
    const write = current.creator.saveName(text, signal); writes.current = write;
    void write.then((value) => {
      if (!signal.aborted && serial === editID.current) { setState({ ...current, value }); setSaveFailed(false); setError(false); }
    }, () => { if (!signal.aborted && serial === editID.current) { setSaveFailed(true); setError(true); } });
  }
  async function create(another = false) {
    if (!current || inFlight.current) return;
    const signal = lifetime.current!.signal;
    inFlight.current = true; setBusy(true); setError(false);
    try {
      await writes.current;
      const value = another ? await current.creator.startAnother(current.value.created?.id ?? current.value.unavailableCreatedId ?? "", signal) : await current.creator.create(name, signal);
      if (!signal.aborted) { setState({ ...current, value }); setName(value.name); }
    } catch {
      if (!signal.aborted) {
        setError(true);
        try {
          const value = await current.creator.read(signal);
          if (!signal.aborted && value.name === name) setState({ ...current, value });
        } catch { /* Preserve the visible name and uncertain retry. */ }
      }
    } finally { if (!signal.aborted) { inFlight.current = false; setBusy(false); } }
  }
  return <div className="my-5 max-w-xl text-sm text-[#52606e]" aria-label="Create a conversation">
    {current?.value.unavailableCreatedId ? <><p>The previous conversation is no longer available here.</p><Button className="mt-2" variant="ghost" disabled={busy} onClick={() => void create(true)}>Start another conversation</Button></> : current?.value.created ? <div className="flex flex-wrap items-center gap-4">
      <a className="font-medium text-[#20272b] underline" href={transcriptUrl(current.creator.target(current.value))}>Open {current.value.created.name}</a>
      <Button variant="ghost" disabled={busy} onClick={() => void create(true)}>Start another conversation</Button>
    </div> : current ? <form onSubmit={(event) => { event.preventDefault(); if (!saveFailed && name.trim()) void create(); }}>
      <label htmlFor="new-conversation-name" className="mb-2 block">{selection.sourceThreadId ? "New conversation from this reply" : "New conversation"}</label>
      <div className="flex flex-wrap gap-2">
        <input id="new-conversation-name" placeholder="Conversation name" value={name} disabled={busy || Boolean(current.value.pending)} onChange={(event) => edit(event.target.value)} className="min-w-0 flex-1 rounded-lg border border-[#cfe4f1] bg-white px-3 py-2 text-[#20272b] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#7376a8]" />
        <Button type="submit" disabled={busy || saveFailed || !name.trim()}>{current.value.pending ? "Retry creation" : "Create conversation"}</Button>
      </div>
    </form> : !error ? <p role="status">Loading saved conversation…</p> : null}
    {busy ? <p role="status" className="mt-2">Saving conversation…</p> : null}
    {error ? <><p role="alert" className="mt-2">{saveFailed ? "This name couldn't be saved. Save it before creating the conversation." : "Creation couldn't be confirmed. Reload the saved conversation or retry it."}</p>
      {saveFailed ? <Button variant="ghost" disabled={busy} onClick={() => edit(name)}>Save name</Button> : null}
      <Button variant="ghost" disabled={busy} onClick={() => setReload((value) => value + 1)}>Reload saved conversation</Button>
    </> : null}
  </div>;
}
